import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import profileStaff from "../../../assets/images/images.jfif";
import {
  Phone,
  Mail,
  Calendar,
  Star,
  Wallet,
  ClipboardList,
  AlertTriangle,
  ChevronDown,
  Pencil,
  MessageSquarePlus,
  StickyNote,
  Car,
  Wrench,
  CreditCard,
  ShieldCheck,
  Plus,
  List,
} from "lucide-react";
import ClientOverviewTab from "./tabs/ClientOverviewTab";
import ClientVehiclesTab from "./tabs/ClientVehiclesTab";
import ClientOrdersTab from "./tabs/ClientOrdersTab";
import VehicleCreation from "../Creation/VehicleCreation"; 
import "./ClientProfile.css";

const API_BASE = "https://estbn.com/EstbnNew/public/api/portal/clients";
const IMAGE_BASE_URL = "https://estbn.com/EstbnNew/public";

const TIER_THRESHOLDS = { gold: 50000, silver: 5000 };

const getClientTier = (totalPoints = 0) => {
  if (totalPoints >= TIER_THRESHOLDS.gold) return "gold";
  if (totalPoints >= TIER_THRESHOLDS.silver) return "silver";
  return "bronze";
};

const TIER_LABEL = {
  gold: "Gold Tier",
  silver: "Silver Tier",
  bronze: "Bronze Tier",
};

const seededValue = (seed, min, max) => {
  const x = Math.sin((seed || 1) * 999) * 10000;
  const frac = x - Math.floor(x);
  return Math.floor(frac * (max - min + 1)) + min;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "vehicles", label: "Vehicles" },
  { key: "orders", label: "Orders" },
  { key: "invoices", label: "Invoices" },
  { key: "payments", label: "Payments" },
  { key: "wallet", label: "Wallet" },
  { key: "requests", label: "Requests" },
  { key: "chat", label: "Chat" },
  { key: "addresses", label: "Addresses" },
  { key: "documents", label: "Documents" },
  { key: "notes", label: "Notes" },
  { key: "activity", label: "Activity Log" },
];

const ACTIONS_MENU = [
  { key: "add-note", label: "Add Note", icon: StickyNote },
  { key: "send-message", label: "Send Message", icon: MessageSquarePlus },
  { key: "suspend", label: "Suspend / Activate Client", icon: AlertTriangle },
];

function VehiclesProfileHeader({
  summary,
  loading,
  onAddVehicle,
  onViewAll,
  isCreating,
}) {
  const fmt = (value) => {
    if (loading && !summary) return "—";
    return Number(value || 0).toLocaleString("en-US");
  };

  return (
    <div className="cv-profile-header">
       {/* Top */}
      <div className="cv-profile-header-top">
        <div>
          <h1>{isCreating ? "Add Vehicle" : "Vehicles"}</h1>
          <p>
            {isCreating
              ? "Fill in the vehicle details for this client"
              : "Manage client vehicles and view their history"}
          </p>
        </div>
        {isCreating ? (
          <button className="cv-view-all-btn" onClick={onViewAll}>
            <List size={15} />
            View all vehicles
          </button>
        ) : (
          <button className="cv-add-vehicle-btn" onClick={onAddVehicle}>
            <Plus size={15} />
            Add Vehicle
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="cv-profile-stats">
        <div className="cv-profile-stat">
          <div>
            <span className="cv-stat-label">Total Vehicles</span>
            <strong>{fmt(summary?.vehiclesCount)}</strong>
          </div>
          <div className="cv-stat-icon cv-stat-icon--green">
            <Car size={22} />
          </div>
        </div>

        <div className="cv-profile-stat">
          <div>
            <span className="cv-stat-label">Primary Vehicle</span>
            <strong>{fmt(summary?.primaryCount)}</strong>
          </div>
          <div className="cv-stat-icon cv-stat-icon--green">
            <ShieldCheck size={22} />
          </div>
        </div>

        <div className="cv-profile-stat">
          <div>
            <span className="cv-stat-label">Total Orders</span>
            <strong>{fmt(summary?.totalOrders)}</strong>
          </div>
          <div className="cv-stat-icon cv-stat-icon--green">
            <ClipboardList size={22} />
          </div>
        </div>

        <div className="cv-profile-stat">
          <div>
            <span className="cv-stat-label">Total Spent</span>
            <strong>
              {fmt(summary?.totalSpent)} <small>EGP</small>
            </strong>
          </div>
          <div className="cv-stat-icon cv-stat-icon--green">
            <CreditCard size={22} />
          </div>
        </div>

        <div className="cv-profile-stat">
          <div>
            <span className="cv-stat-label">Upcoming Services</span>
            <strong>{fmt(summary?.upcomingOrders)}</strong>
          </div>
          <div className="cv-stat-icon cv-stat-icon--green">
            <Wrench size={22} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientProfile() {

  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState(null);
  const [vehiclesSummary, setVehiclesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [actionsOpen, setActionsOpen] = useState(false);

  const [showCreateVehicle, setShowCreateVehicle] = useState(false);
  const [vehiclesRefreshKey, setVehiclesRefreshKey] = useState(0);

  useEffect(() => {
    if (activeTab !== "vehicles") setShowCreateVehicle(false);
  }, [activeTab]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [clientRes, ordersRes, requestsRes] = await Promise.all([
        axios.get(`${API_BASE}/${id}`, { headers }),
        axios.get(`${API_BASE}/${id}/orders`, {
          headers,
          params: { per_page: 5 },
        }),
        axios.get(`${API_BASE}/${id}/requests`, {
          headers,
          params: { per_page: 50 },
        }),
      ]);
      
      setClient(clientRes.data?.data.client ?? null);
      setOrders(ordersRes.data?.data?.orders ?? []);
      setRequests(requestsRes.data?.data?.requests ?? []);
    } catch (err) {
      console.error("Error loading client profile:", err);
      setError("حدث خطأ أثناء تحميل بيانات العميل");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (activeTab !== "vehicles" || !id || !token) return;

const fetchVehicles = async () => {
  setVehiclesLoading(true);
  setVehiclesError(null);

  try {
    const response = await axios.get(`${API_BASE}/${id}/vehicles`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = response.data?.data;
    setVehicles(data?.vehicles || []);
    setVehiclesSummary({
      vehiclesCount: data?.vehicles_count ?? 0,
      primaryCount: data?.primary_vehicles_count ?? 0,
      totalOrders: data?.total_orders_count ?? 0,
      totalSpent: data?.total_spent ?? 0,
      upcomingOrders: data?.upcoming_orders_count ?? 0,
    });
  } catch (err) {
    console.error("Error loading client vehicles:", err);
    setVehiclesError("حدث خطأ أثناء تحميل سيارات العميل");
    setVehicles([]);
    setVehiclesSummary(null);
  } finally {
    setVehiclesLoading(false);
  }
};

    fetchVehicles();
  }, [activeTab, id, token, vehiclesRefreshKey]);

  if (loading) {
    return (
      <div className="cpf-page cpf-state">
        <span className="cpf-spinner" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="cpf-page cpf-state">
        <AlertTriangle size={32} />
        <p>{error || "تعذر تحميل بيانات العميل"}</p>
      </div>
    );
  }

  const tier = getClientTier(client.points?.total_points);
  const isLoyal = (client.orders_count || 0) >= 5;

  const renderTab = () => {
    switch (activeTab) {
      case "overview":
        return (
          <ClientOverviewTab
            client={client}
            orders={orders}
            requests={requests}
            onGoToTab={setActiveTab}
          />
        );
      case "vehicles":
        if (showCreateVehicle) {
          return (
            <VehicleCreation
              clientId={id}
              onCancel={() => setShowCreateVehicle(false)}
              onSuccess={() => {
                setShowCreateVehicle(false);
                setVehiclesRefreshKey((k) => k + 1); 
              }}
            />
          );
        }
        return (
          <ClientVehiclesTab
            client={client}
            vehicles={vehicles}
            loading={vehiclesLoading}
            error={vehiclesError}
          />
        );
      case "orders":
        return <ClientOrdersTab client={client} orders={orders} />;
      default:
        return (
          <div className="cpf-tab-placeholder">
            <p>لسه بنشتغل على التاب ده 🚧</p>
          </div>
        );
    }
  };

  return (
    <div className="cpf-page">
      {/* ---------------- Header ---------------- */}
      {activeTab === "vehicles" ? (
        <VehiclesProfileHeader
          client={client}
          vehicles={vehicles}
          summary={vehiclesSummary}
          loading={vehiclesLoading}
          onAddVehicle={() => setShowCreateVehicle(true)}
          onViewAll={() => setShowCreateVehicle(false)}
          isCreating={showCreateVehicle}
        />
      ) : (
        <div className="cpf-header">
          <div className="cpf-header-main">
            <div className="cpf-header-left">
              <img
                className="cpf-avatar"
                src={
                  client.profile_photo_path
                    ? `${IMAGE_BASE_URL}${client.profile_photo_path}`
                    : profileStaff
                }
                alt={client.name}
              />
              <div className="cpf-header-info">
                <div className="cpf-header-name-row mt-2 mb-1">
                  <h2 title={client.name}>{client.name || "—"}</h2>
                  <span
                    className={`cpf-status-badge ${client.is_active ? "is-active" : "is-suspended"}`}
                  >
                    {client.is_active ? "Active" : "Suspended"}
                  </span>
                </div>

                <div className="cpf-header-meta">
                  {client.phone_number && (
                    <span title={client.phone_number}>
                      <Phone size={13} /> {client.phone_number}
                    </span>
                  )}
                  {client.email && (
                    <span title={client.email}>
                      <Mail size={13} /> {client.email}
                    </span>
                  )}
                </div>
                <div className="cpf-header-meta">
                  <span>{client.reference}</span>
                  <span>
                    <Calendar size={13} /> Client Since:{" "}
                    {formatDate(client.created_at)}
                  </span>
                </div>

                <div className="cpf-header-tags">
                  {isLoyal && (
                    <span className="cpf-dot-tag">Loyal Customer</span>
                  )}
                  <span className={`cpf-dot-tag cpf-dot-tag--${tier}`}>
                    {TIER_LABEL[tier]}
                  </span>
                </div>
              </div>
            </div>
            <div className=" d-flex flex-column gap-3 ">
              <div className="cpf-header-actions-row">
                <div className="cpf-header-actions">
                  <div className="cpf-actions-dropdown">
                    <button
                      className="cpf-btn-outline"
                      onClick={() => setActionsOpen((p) => !p)}
                    >
                      Actions <ChevronDown size={14} />
                    </button>
                    {actionsOpen && (
                      <div className="cpf-actions-menu">
                        {ACTIONS_MENU.map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.key}
                              className="cpf-actions-menu-item"
                              onClick={() => setActionsOpen(false)}
                            >
                              <Icon size={14} /> {item.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <button className="cpf-btn-dark">
                    <Pencil size={15} /> Edit Client
                  </button>
                </div>
              </div>
              <div className="cpf-header-stats">
                <div className="cpf-mini-stat">
                  <div className=" d-flex flex-column align-items-start justify-content-center gap-1">
                    <p className="cpf-mini-label">Total Orders</p>
                    <p className="cpf-mini-value">
                      {(client.overview?.orders_count || 0).toLocaleString(
                        "en-US",
                      )}
                    </p>
                    <button
                      className="cpf-mini-link"
                      onClick={() => setActiveTab("orders")}
                    >
                      View all orders
                    </button>
                  </div>
                  <div className="cpf-mini-icon cpf-mini-icon--indigo">
                    <ClipboardList size={18} />
                  </div>
                </div>

                <div className="cpf-mini-stat">
                  <div className=" d-flex flex-column justify-content-center align-items-start gap-1">
                    <p className="cpf-mini-label">Total Spent</p>
                    <p
                      className="cpf-mini-value"
                      title={
                        (
                          client.overview?.total_orders_value || 0
                        ).toLocaleString("en-US") + " EGP"
                      }
                    >
                      {(
                        client.overview?.total_orders_value || 0
                      ).toLocaleString("en-US")}{" "}
                      EGP
                    </p>
                    <button
                      className="cpf-mini-link"
                      onClick={() => setActiveTab("wallet")}
                    >
                      View financials
                    </button>
                  </div>
                  <div className="cpf-mini-icon cpf-mini-icon--green">
                    <Wallet size={18} />
                  </div>
                </div>

                <div className="cpf-mini-stat">
                  <div className=" d-flex flex-column align-items-start justify-content-center gap-1">
                    <p className="cpf-mini-label">Requests</p>
                    <p className="cpf-mini-value">
                      {(client.overview?.requests_count || 0).toLocaleString(
                        "en-US",
                      )}
                    </p>
                    <button
                      className="cpf-mini-link"
                      onClick={() => setActiveTab("requests")}
                    >
                      View all requests
                    </button>
                  </div>
                  <div className="cpf-mini-icon cpf-mini-icon--red">
                    <AlertTriangle size={18} />
                  </div>
                </div>

                <div className="cpf-mini-stat">
                  <div className=" d-flex flex-column justify-content-center gap-1">
                    <p className="cpf-mini-label">Loyalty Points</p>
                    <p className="cpf-mini-value">
                      {(client.points?.total_points || 0).toLocaleString(
                        "en-US",
                      )}
                    </p>
                    <span className={`cpf-mini-tag cpf-mini-tag--${tier}`}>
                      {TIER_LABEL[tier]}
                    </span>
                  </div>
                  <div className="cpf-mini-icon cpf-mini-icon--gold">
                    <Star size={18} />
                  </div>
                </div>

                <div className="cpf-mini-stat">
                  <div className=" d-flex flex-column justify-content-center align-items-start gap-1">
                    <p className="cpf-mini-label">
                      Wallet Balance{" "}
                      <span
                        className="cpf-sample-dot"
                        title="Estimated — wallet balance isn't returned by the API yet"
                      />
                    </p>
                    <p className="cpf-mini-value">
                      {seededValue(client.id * 3, 0, 5000).toLocaleString(
                        "en-US",
                      )}{" "}
                      EGP
                    </p>
                    <button
                      className="cpf-mini-link"
                      onClick={() => setActiveTab("wallet")}
                    >
                      View wallet
                    </button>
                  </div>
                  <div className="cpf-mini-icon cpf-mini-icon--blue">
                    <Wallet size={18} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ---------------- Tabs ---------------- */}
      <div className="cpf-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`cpf-tab-btn ${activeTab === tab.key ? "is-active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ---------------- Tab content ---------------- */}
      <div className="cpf-tab-content">{renderTab()}</div>
    </div>
  );
}
